// sort-imports-ignore
import { FB_PIXEL_ID } from '@dupe/common/constants'
import { GoogleAnalytics } from '@next/third-parties/google'
import * as Sentry from '@sentry/nextjs'
import { Analytics } from '@vercel/analytics/react'
import airbridge from 'airbridge-web-sdk-loader'
import { getCookie } from 'cookies-next'
import { IKContext } from 'imagekitio-react'
import PlausibleProvider from 'next-plausible'
import {
  DefaultSeo,
  OrganizationJsonLd,
  SoftwareAppJsonLd,
  WebPageJsonLd,
} from 'next-seo'
import { ThemeProvider } from 'next-themes'
import type { AppProps } from 'next/app'
import Router from 'next/router'
import Script from 'next/script'
import posthog, { BootstrapConfig } from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect, useMemo } from 'react'
import { IntercomProvider } from 'react-use-intercom'
import ErrorPage from '~/src/components/ErrorPage'
import { AppProvider } from '~/src/context/AppProvider'
import AuthProvider from '~/src/context/AuthProvider'
import ExtensionModalProvider from '~/src/context/ExtensionModalProvider'
import { SearchProvider } from '~/src/context/SearchProvider'
import { mono_font, sans_font, serif_font } from '~/src/fonts'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import {
  IMAGEKIT_PUBLIC_KEY,
  IMAGEKIT_URL_ENDPOINT,
  DUPE_FLAGS_COOKIE_KEY,
} from '~/src/lib/constants'
import authenticator from '~/src/lib/imagekit/authenticator'
import DEFAULT_SEO from '~/src/next-seo.config'
import { DupeInputStoreProvider } from '~/src/providers/DupeInputStoreProvider'

// CSS imports
import '~/public/vendor/smartbanner/smartbanner.css'
import '~/public/vendor/judgeme/judgeme.overrides.css'
import '~/src/styles/globals.css'

// create custom script component with tb_customer_id attribute
interface CustomScriptProps extends React.ComponentProps<'script'> {
  tb_customer_id: string
}

const CustomScript: React.FC<CustomScriptProps> = ({ ...props }) => {
  return <Script {...props} />
}

// const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID
const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID

if (
  typeof window !== 'undefined' &&
  process.env.NEXT_PUBLIC_AIRBRIDGE_WEB_TOKEN
) {
  airbridge.init({
    app: 'dupecom',
    webToken: process.env.NEXT_PUBLIC_AIRBRIDGE_WEB_TOKEN,
    user: {
      alias: {
        posthog_distinct_id: posthog.get_distinct_id(),
      },
    },
  })
}

// Add this function before the App component
const calculateReviewCount = () => {
  // Use a consistent reference date to avoid SSR/CSR mismatches
  const startDate = new Date('2024-12-02').getTime()
  const startingCount = 2209
  const dailyIncrease = 3

  // Use server-side rendering timestamp or current time
  const now =
    typeof window === 'undefined'
      ? startDate // During build/SSR, use start date
      : Date.now()

  const diffDays = Math.floor((now - startDate) / (1000 * 60 * 60 * 24))
  return startingCount + Math.max(0, diffDays) * dailyIncrease
}

// App data for JSON-ld schema
const appData = {
  name: 'Dupe.com | Find similar products for less',
  url: 'https://apps.apple.com/us/app/dupe-com-up-to-90-savings/id1605692440',
  description: 'Up to 90% savings on furniture, fashion, and more.',
  operatingSystem: 'iOS',
  applicationCategory: 'ShoppingApplication',
  aggregateRating: {
    ratingValue: '4.9',
  },
  price: '0',
  priceCurrency: 'USD',
}

export default function App({ Component, pageProps }: AppProps) {
  const reviewCount = useMemo(calculateReviewCount, [])
  const { tempUserId } = useGetTempUserId()
  const { authUser, loading: loadingAuthUser } = useAuthUser()

  // posthog init
  useEffect(() => {
    let flagsBootstrap: BootstrapConfig = {}
    try {
      flagsBootstrap = JSON.parse(
        (getCookie(DUPE_FLAGS_COOKIE_KEY) || '{}') as string
      )
    } catch (e) {
      // ignore
    }

    posthog.debug(false)
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host:
        process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      bootstrap: flagsBootstrap,
      person_profiles: 'identified_only',
      disable_session_recording: true,
      autocapture: false,
      opt_in_site_apps: false, // TODO: come back to this. seems to be causing nextjs rehydration issues via posthog proxy
      capture_heatmaps: true,
      capture_performance: true,
      // // Enable debug mode in development
      // loaded: (posthog) => {
      //   if (process.env.NODE_ENV === 'development') posthog.debug()
      // },
    })
  }, [])

  // ensure we always set the distinct id to the auth user id or temp user id
  // this will be called again with more user details if we get them via the auth provider, in useAnalytics
  useEffect(() => {
    const distinctId = authUser?.uid || tempUserId
    const posthogDistinctId = posthog.get_distinct_id()
    if (distinctId && posthogDistinctId !== distinctId) {
      posthog.identify(distinctId)
    }
  }, [authUser?.uid, tempUserId])

  // route change handler
  useEffect(() => {
    if (loadingAuthUser) return

    const __dupe_temp_user_id = tempUserId
    const __dupe_auth_user_id = authUser?.uid
    const _ga = getCookie('_ga')
    const _gcl_aw = getCookie('_gcl_aw')

    const properties = {
      ...(__dupe_temp_user_id ? { __dupe_temp_user_id } : {}),
      ...(__dupe_auth_user_id ? { __dupe_auth_user_id } : {}),
      ...(_ga ? { _ga } : {}),
      ...(_gcl_aw ? { _gcl_aw } : {}),
    }

    const handleRouteChange = () => {
      posthog?.capture('$pageview', properties)

      // Reddit Pixel - track page visit
      if (window?.rdt) {
        window.rdt('track', 'PageVisit')
      }

      // Facebook Pixel - track page view
      // https://developers.facebook.com/docs/pages/official-events/pixel-implementation-guide/
      if (window?.fbq) {
        window.fbq('track', 'PageView')
      }

      // Podscribe - track page view
      if (window?.podscribe) {
        window?.podscribe('view', { device_id: authUser?.uid || tempUserId })
      }
    }

    Router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [tempUserId, authUser?.uid, loadingAuthUser])

  return (
    <>
      <style jsx global>{`
        :root {
          --font-sans: ${sans_font.style.fontFamily};
          --font-serif: ${serif_font.style.fontFamily};
          --font-mono: ${mono_font.style.fontFamily};
        }
      `}</style>

      <OrganizationJsonLd
        id='https://dupe.com'
        name='Dupe.com'
        url='https://dupe.com'
      />

      <WebPageJsonLd
        id='https://dupe.com'
        url='https://dupe.com'
        title={DEFAULT_SEO.defaultTitle}
        description={DEFAULT_SEO.description}
      />

      {/* // json ld for MobileApplication */}
      <Script
        id='jsonld-mobile-app'
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: `
        {
          "@context": "https://schema.org",
          "@type": "MobileApplication",
          "name": "${appData.name}",
          "url": "${appData.url}",
          "description": "${appData.description}",
          "operatingSystem": "${appData.operatingSystem}",
          "applicationCategory": "${appData.applicationCategory}",
          "offers": {
            "@type": "Offer",
            "price": "${appData.price}",
            "priceCurrency": "${appData.priceCurrency}"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "reviewCount": "${reviewCount}",
            "ratingValue": "${appData.aggregateRating.ratingValue}"
          }
        }
        `,
        }}
      />

      <SoftwareAppJsonLd
        name={appData.name}
        url={appData.url}
        description={appData.description}
        operatingSystem={appData.operatingSystem}
        applicationCategory={appData.applicationCategory}
        aggregateRating={{
          ratingValue: appData.aggregateRating.ratingValue,
          reviewCount: reviewCount,
        }}
        price={appData.price}
        priceCurrency={appData.priceCurrency}
      />

      <ThemeProvider attribute='class'>
        <IntercomProvider
          appId={process.env.NEXT_PUBLIC_INTERCOM_APP_ID as string}
          initializeDelay={1000}
          shouldInitialize={!!process.env.NEXT_PUBLIC_VERCEL_ENV}
          // shouldInitialize
        >
          <Sentry.ErrorBoundary
            onError={(err) => {
              Sentry.captureException(err, {
                contexts: {
                  extra: {
                    type: 'ErrorBoundary',
                    pageProps,
                  },
                },
              })
            }}
            fallback={<ErrorPage />}
          >
            <PostHogProvider client={posthog}>
              <IKContext
                urlEndpoint={IMAGEKIT_URL_ENDPOINT}
                publicKey={IMAGEKIT_PUBLIC_KEY}
                authenticator={authenticator}
              >
                <PlausibleProvider domain='dupe.com' trackOutboundLinks>
                  <AppProvider hash={pageProps?.hash}>
                    <AuthProvider>
                      <ExtensionModalProvider>
                        <SearchProvider>
                          <DupeInputStoreProvider>
                            {/* {GTM_ID && <GoogleAnalytics gtmId={GTM_ID} />} */}
                            {GA_MEASUREMENT_ID && (
                              <>
                                <GoogleAnalytics gaId={GA_MEASUREMENT_ID} />
                                <Script id='strackr-snippet'>
                                  {`
                                window.strackr=window.strackr||function(){(window.strackr.q = window.strackr.q || []).push(arguments)};
                                window.strackr('create', 'ga', 'G7oLG');
                                window.strackr('setting', 'customposition', 'right');
                                window.strackr('update');
                                `}
                                </Script>
                                <Script
                                  async
                                  src='/vendor/strackr/bundle.min.js'
                                />
                              </>
                            )}
                            <DefaultSeo {...DEFAULT_SEO} />
                            <Component {...pageProps} />
                            <Analytics debug={false} />
                          </DupeInputStoreProvider>
                        </SearchProvider>
                      </ExtensionModalProvider>
                    </AuthProvider>
                  </AppProvider>
                </PlausibleProvider>
              </IKContext>
            </PostHogProvider>
          </Sentry.ErrorBoundary>
        </IntercomProvider>
      </ThemeProvider>

      {/* Tinybird */}
      {!!tempUserId && (
        <CustomScript
          defer
          src='/vendor/flock.js@1.4.1.js'
          data-host='https://api.us-east.tinybird.co'
          tb_customer_id={tempUserId}
          data-token='p.eyJ1IjogImI0ZTMyMDlmLTVjMGYtNDExOC1iMzk4LWE5MDRiOWE4YTI3YiIsICJpZCI6ICIwZGFlNjA5ZS1kMjRlLTQxZGUtODI1Zi1kMmEwMGQzNmI2MDMiLCAiaG9zdCI6ICJ1c19lYXN0In0.P7bemzO0U4mw8LytlarDleFm9L70MCdFSvrbuYN7v7Y'
        />
      )}
      {/* End Tinybird */}

      {/* ClickCease */}
      <Script
        id='clickcease'
        strategy='afterInteractive'
        src='https://www.clickcease.com/monitor/stat.js'
      />
      <noscript>
        <a href='https://www.clickcease.com' rel='nofollow'>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src='https://monitor.clickcease.com'
            alt='ClickCease'
            width='1'
            height='1'
          />
        </a>
      </noscript>
      {/* End ClickCease */}

      {/* WeCanTrack */}
      <Script
        id='wct'
        dangerouslySetInnerHTML={{
          __html: `
            (function(w, c, t, u) {
              w._wct = w._wct || {}; w._wct = u;
              var s = c.createElement(t);
              s.type = 'text/javascript'; s.async = true; s.src = 'https://wct-2.com/wct.js?type=session';
              var r = c.getElementsByTagName(t)[0];
              r.parentNode.insertBefore(s, r);
            }(window, document, 'script', {
              'uid': 'JswVnJ',
              'proxy': 'https://wct-2.com',
              'auto_tagging': false
            }));
          `,
        }}
      />
      {/* End WeCanTrack */}

      {/* Reddit Pixel */}
      <Script
        id='reddit-pixel'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
          !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
          rdt('init','a2_g41eobw3irth');
          rdt('track', 'PageVisit'); // track initial page view
          `,
        }}
      />
      {/* End Reddit Pixel */}

      {/* Facebook Pixel */}
      <Script
        id='fb-pixel-base'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${FB_PIXEL_ID}');
            fbq('track', 'PageView'); // track initial page view
          `,
        }}
      />
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt=''
          height='1'
          width='1'
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`}
        />
      </noscript>
      {/* End Facebook Pixel Scripts */}

      {/* Podscribe */}
      {!loadingAuthUser && (!!authUser?.uid || !!tempUserId) && (
        <Script
          id='podscribe-script'
          dangerouslySetInnerHTML={{
            __html: `
          (function (w, d) {
              var id = 'podscribe-capture',
              n = 'script';
              var e = d.createElement(n);
              e.id = id;
              e.async = true;
              e.src = 'https://d34r8q7sht0t9k.cloudfront.net/tag.js';
              var s = d.getElementsByTagName(n)[0];
              s.parentNode.insertBefore(e, s);
              e.addEventListener('load', function() {
                w.podscribe('init', { user_id: 'c2b8a3cd-9178-4a43-a255-5a61eba7bab1', advertiser: 'dupe' });
                w.podscribe('view', { device_id: '${authUser?.uid || tempUserId}' }); // track initial page view
              })
            })(window, document);
          `,
          }}
        />
      )}
      {/* End Podscribe */}
    </>
  )
}
